/**
 * Every threshold the vocal scoring library uses, with the musical reason it holds that value.
 *
 * These are initial values from the spec, to be re-derived against the ground-truth fixture set
 * before the tool ships. A guessed constant in a scoring tool fails quietly and in the user's
 * favour, which is the worst way for a measurement to be wrong.
 * @see docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5, §8
 */

/**
 * A frame counts as sung only at this clarity or above. Clarity rejects material that is loud
 * but aperiodic — a fan, a keyboard, traffic — which no RMS test can tell from a voice.
 * @see spec §5.1
 */
export const voicingClarityMin = 0.9

/**
 * ...and only this many dB above the measured noise floor. The RMS test rejects periodic
 * material too quiet to be the user: a neighbour's music, mains hum. 12 dB is a quadrupling of
 * amplitude — more than room tone varies, less than the margin between a room and someone
 * singing into it.
 * @see spec §5.1
 */
export const voicingFloorDb = 12

/**
 * The noise floor is the median RMS of this much of the head of the capture window, recorded
 * before the user is cued to sing. Median, not mean, so one door slam does not raise the gate
 * for the whole phrase.
 * @see spec §5.1
 */
export const noiseFloorWindowMs = 500

/**
 * Floor under the measured noise floor. A digitally silent lead-in — a muted input, a virtual
 * device — measures 0, and every later frame would then clear a 12 dB margin over nothing.
 * 1e-5 is ~-100 dBFS, below any live microphone's self-noise.
 */
export const minNoiseFloorRms = 1e-5

/**
 * A pitch move of this many cents, held, is a new note rather than a wobble on the old one.
 * 50 cents is the half-semitone point: below it the singer is still on the same note, above it
 * they have moved to a different one.
 * @see spec §5.2
 */
export const boundaryCents = 50

/**
 * ...and it must hold for this long. A duration, not a frame count, and long enough to outlast
 * a vibrato excursion.
 *
 * A sinusoidal modulation of amplitude A spends the part of each period where |A sin| exceeds
 * the 50 c boundary off the running median. For a wide operatic vibrato — A = 100 c at 5 Hz —
 * that is a third of a 200 ms period, about 67 ms, or 3.1 frames at the 21 ms hop: a hold of
 * three *frames* trips on every swing, and one sustained note shatters into dozens.
 *
 * 110 ms outlasts the excursion of any modulation up to A = 120 c at 4.5 Hz or above
 * (|sin| >= 0.417 for 36 % of a 222 ms period = 81 ms), while still splitting a genuine step,
 * which never comes back. It costs nothing in latency: `restart` seeds the new note with the
 * frames that were pending, so the new note keeps its own onset.
 * @see spec §5.2
 */
export const boundaryHoldMs = 110

/**
 * An unvoiced gap this long also ends a note. Short enough to catch a re-articulated repeated
 * note — two crotchets on the same pitch are otherwise one long note — and long enough not to
 * split a note at a consonant or a breath catch.
 * @see spec §5.2
 */
export const gapMs = 80

/**
 * The head of every note that is discarded before measurement. Singers scoop and slide into
 * pitch; the onset portamento is technique, not error, and scoring it would penalise exactly
 * the singers doing it deliberately. These frames still belong to the note — they set its
 * `startTime` — they simply do not vote on its pitch or on where it ends.
 * @see spec §5.2
 */
export const scoopMs = 100

/**
 * Cost of leaving one note unmatched in the alignment — a reference note the singer missed, or
 * a note they sang that is not in the reference.
 * @see alignMaxMatchCost, which this value is chosen against
 * @see spec §5.3
 */
export const alignSkipCost = 2

/**
 * A match costs the distance between the two notes in semitones, capped here.
 *
 * The cap is what makes §5.3 and §5.7 agree. Because it is below `2 * alignSkipCost`, pairing
 * two notes is never dearer than declaring one missed and one extra — so a singer who sang the
 * right *number* of notes always gets a 1:1 reading, and a note they flipped an octave is
 * reported as the wrong note it is rather than dissolving into a hole plus a spurious extra.
 * "Missed" and "extra" then appear only when the note counts actually differ, which is what
 * those two words mean to a singer.
 *
 * Past a tone and a half the alignment stops caring how wrong a note is; the scoring layer,
 * which does care, still sees the exact cents.
 * @see spec §5.3, §5.7
 */
export const alignMaxMatchCost = 3

/**
 * "On the note". 20 cents is a fifth of a semitone — inside it a listener hears the reference
 * pitch, not a version of it.
 * @see spec §5.4
 */
export const onBandCents = 20

/**
 * "A little under/over". Past 40 cents the deviation is plainly audible as being under or over,
 * rather than as a colour on the note.
 * @see spec §5.4
 */
export const closeBandCents = 40

/**
 * Full marks at or under this deviation. 10 cents is roughly the just-noticeable difference for
 * melodic pitch, and it is the tolerance the existing tuner already treats as in tune.
 * @see spec §5.4
 */
export const fullScoreCents = 10

/**
 * Zero marks at this deviation and beyond. 50 cents is the semitone boundary: past it the
 * *note* is wrong, and the note-level judgement takes over from the tuning-level one.
 * @see spec §5.4
 */
export const zeroScoreCents = 50

/**
 * How far from an exact octave a note may sit and still count toward the whole-phrase octave
 * rule. A phrase is only called octave-displaced when *every* paired note is within this of
 * +/-12 semitones — a bass singing a soprano phrase down an octave has sung it correctly, but a
 * single flipped note in an otherwise at-pitch phrase is a melodic error and is scored as one.
 * @see spec §5.7
 */
export const octaveToleranceCents = 50

/**
 * Interquartile range above which a note is reported as "wavering" rather than "steady".
 *
 * The IQR of a sinusoidal modulation of amplitude A is 1.414 x A — its quartiles sit at
 * +/- A sin(pi/4). A healthy vibrato runs A = 20-40 c in pop and 50-60 c in classical, so the
 * widest healthy vibrato measures 1.414 x 60 = 84.9 c. 90 c clears that with ~5 c of margin for
 * the sample variance of a short note (a 1.5 s A = 55 c vibrato samples 79.7 c against a
 * population 77.8 c). A 30 c threshold would have called a modest pop vibrato (A = 30 c,
 * IQR 42 c) "wavering": exactly the insult §5.6 forbids.
 *
 * Musically, 90 c means "wavering" is a note wandering more than a semitone and a quarter
 * peak-to-peak (the crossover is A = 63.6 c), which no teacher would call vibrato.
 *
 * The tracker never sees the singer's full amplitude. A 2048-sample window is 42.7 ms, which
 * averages the pitch over 23 % of a 5.5 Hz vibrato period and attenuates it by
 * sinc(pi f W) = 0.911 at that rate, and further at faster ones. The measured IQR is therefore
 * 1.414 x A x sinc(pi f W) — a predicted 77.3 c for A = 60 c at 5.5 Hz, against 76.3-77.7 c
 * measured on Glenn's fixtures. So 90 c is *more* forgiving in the singer's domain than the
 * crossover above suggests: at 5.5 Hz it is a real vibrato of A = 70 c, and higher still at
 * faster rates. That is the safe direction for §5.6, and the value stands.
 *
 * The IQR cannot separate periodic vibrato from aperiodic wander — a singer drifting randomly
 * across the same span scores the same. Telling those two apart needs the periodicity of the
 * frame residual, which is a v2 improvement and not a different number here.
 * @see spec §5.6 — this tool has no business telling a singer their vibrato is wrong
 */
export const stabilityIqrCents = 90

/**
 * The fraction of the calibrated range an exercise is transposed into — the middle 60 %.
 *
 * Without this the tool measures range, not pitch: a phrase centred on A4 is comfortable for a
 * soprano and unsingable for a bass, and the bass's score would record a failure of tessitura
 * as a failure of ear. The outer 40 % is left out because that is where a singer's extremes
 * live, and a scoring tool must not ask for them.
 * @see spec §5.8
 */
export const comfortableRangeFraction = 0.6
